import { PRODUCT_NAME } from "@src/shared/brand";
import { navigationV2 } from "@src/shared/navigation/v2";

import type { SignUpProvisioningView } from "../auth.schemas";
import type {
  ProvisioningStatus,
  SignUpProvisioningSnapshot,
} from "./auth.provisioning.machine";

/**
 * Must stay above Better Auth's 10s rate limit window for `/sign-up*` paths
 * (3 requests per window). That counter only resets once a request arrives more
 * than a window after the previous one, so any faster cadence accumulates
 * instead of resetting and the fourth poll onwards gets a 429 — which the
 * machine reports as a failed signup.
 */
export const PROVISIONING_POLL_INTERVAL_MS = 12_000;
export const PROVISIONING_PENDING_TIMEOUT_MS = 10 * 60 * 1_000;
export const EMAIL_VERIFICATION_POLL_INTERVAL_MS = 2_500;
export const PROVISIONING_SUPPORT_EMAIL = "support@elationemr.com";

/**
 * Delays (relative to entering each `provisioning.pending` sub-state) at which
 * the reassuring message rotates. The machine models these as delayed
 * transitions, so message timing lives in one place instead of a render-time
 * clock.
 */
export const PROVISIONING_MESSAGE_SETTING_UP_MS = 5_000;
export const PROVISIONING_MESSAGE_FINALIZING_MS = 10_000;
export const PROVISIONING_MESSAGE_SLOW_MS = 15_000;

export type ProvisioningPendingStage =
  | "creating"
  | "settingUp"
  | "finalizing"
  | "slow";

export const PROVISIONING_PENDING_MESSAGES: Record<
  ProvisioningPendingStage,
  string
> = {
  creating: "Creating your account...",
  settingUp: "Setting up your practice...",
  finalizing: "Almost ready - finalizing your account...",
  slow: "This is taking longer than usual. Hang tight.",
};

export type ProvisioningFailureView = {
  title: string;
  description: string;
  requestId?: string;
  supportHref: string;
  signInRecommended?: boolean;
};

export function buildProvisioningSupportHref(requestId?: string): string {
  const params = new URLSearchParams();
  params.set("subject", `${PRODUCT_NAME.singular} Sign Up Issue`);
  params.set(
    "body",
    requestId
      ? `Reference: ${requestId}`
      : "I need help finishing account setup.",
  );

  return `mailto:${PROVISIONING_SUPPORT_EMAIL}?${params.toString()}`;
}

export function getProvisioningFailureView(
  status: ProvisioningStatus & { requestId?: string },
): ProvisioningFailureView {
  const requestId = status.requestId;
  const base = {
    requestId,
    supportHref: buildProvisioningSupportHref(requestId),
  };
  const failureCode = status.failureReason?.code;

  if (failureCode === "user_already_exists") {
    return {
      ...base,
      title: "This email is already registered with Elation.",
      description: "Sign in instead or use a different email.",
      signInRecommended: true,
    };
  }

  if (failureCode === "validation_error") {
    return {
      ...base,
      title: "Account setup failed",
      description:
        "We couldn't validate your information. Please contact support and reference the ID below.",
    };
  }

  return {
    ...base,
    title: "Account setup failed",
    description:
      "Something went wrong while setting up your account. Please contact support and reference the ID below.",
  };
}

function getPendingStage(
  snapshot: SignUpProvisioningSnapshot,
): ProvisioningPendingStage {
  if (snapshot.matches({ provisioning: { pending: "slow" } })) return "slow";
  if (snapshot.matches({ provisioning: { pending: "finalizing" } })) {
    return "finalizing";
  }
  if (snapshot.matches({ provisioning: { pending: "settingUp" } })) {
    return "settingUp";
  }

  return "creating";
}

/**
 * Pure projection of the sign-up machine snapshot into the view-model the
 * sign-up UI renders. All provisioning-state interpretation lives here (and in
 * the failure helpers), so the hook and components hold no branching.
 */
export function selectProvisioningView(
  snapshot: SignUpProvisioningSnapshot,
): SignUpProvisioningView {
  if (
    snapshot.matches({ provisioning: "succeeded" }) ||
    snapshot.matches({ provisioning: "completing" }) ||
    snapshot.matches("emailVerification") ||
    snapshot.matches("done")
  ) {
    return { state: "succeeded", message: "Account setup complete." };
  }

  if (snapshot.matches({ provisioning: "failed" })) {
    const failureStatus = snapshot.context.provisioningStatus;

    if (failureStatus?.state === "failed") {
      const failureView = getProvisioningFailureView(failureStatus);

      return {
        state: "failed",
        ...failureView,
        signInHref: failureView.signInRecommended
          ? navigationV2.login()
          : undefined,
      };
    }

    const requestId = snapshot.context.unexpectedErrorReference ?? undefined;

    return {
      state: "failed",
      title: "Account setup failed",
      description: requestId
        ? "Something went wrong while setting up your account. Please contact support and reference the ID below."
        : "Something went wrong while setting up your account. Please contact support for help.",
      requestId,
      supportHref: buildProvisioningSupportHref(requestId),
    };
  }

  if (snapshot.matches({ provisioning: "timedOut" })) {
    return {
      state: "failed",
      title: "Account setup is taking unusually long",
      description: "Please contact support for help finishing account setup.",
      supportHref: buildProvisioningSupportHref(undefined),
    };
  }

  if (snapshot.matches("provisioning")) {
    return {
      state: "pending",
      message: PROVISIONING_PENDING_MESSAGES[getPendingStage(snapshot)],
    };
  }

  return { state: "idle" };
}
